import { StorageManager } from "./storage-manager.js";

// Data manipulation functions
function utf8(str: string) {
    return new TextEncoder().encode(str);
}

function base64url(bytes: Uint8Array) {
    const binary = Array.from(bytes)
        .map(b => String.fromCharCode(b))
        .join("");

    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

function randomHex(bytes = 32) {
    const buf = crypto.getRandomValues(
        new Uint8Array(bytes)
    );

    return Array.from(buf)
        .map(x => x.toString(16).padStart(2, "0"))
        .join("");
}

// PKCE
async function createPKCE() {
    const verifier = randomHex(64);
    const hash = await crypto.subtle.digest(
        "SHA-256",
        utf8(verifier)
    );

    return {
        verifier,
        challenge: base64url(new Uint8Array(hash))
    };
}

// DPoP
async function createDPoPKeys() {
    const pair = await crypto.subtle.generateKey(
        {
            name: "ECDSA",
            namedCurve: "P-256"
        },
        true,
        ["sign", "verify"]
    );

    const publicJwk = await crypto.subtle.exportKey(
        "jwk",
        pair.publicKey
    );
    const privateJwk = await crypto.subtle.exportKey(
        "jwk",
        pair.privateKey
    );

    return {
        publicJwk,
        privateJwk
    };
}

async function importPrivateKey(jwk: JsonWebKey) {
    return crypto.subtle.importKey(
        "jwk",
        jwk,
        {
            name: "ECDSA",
            namedCurve: "P-256"
        },
        true,
        ["sign"]
    );
}

// JWT
function getJwtPayload(jwt: string) {
    const [, payload] = jwt.split(".");

    if (!payload) {
        throw new Error(`Invalid JWT: ${jwt}`);
    }

    return JSON.parse(atob(payload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
    ));
}
async function signJwt(
    privateKey: CryptoKey,
    header: object,
    payload: object,
) {
    const encodedHeader = base64url(
        utf8(JSON.stringify(header))
    );
    const encodedPayload = base64url(
        utf8(JSON.stringify(payload))
    );
    const signingInput = `${encodedHeader}.${encodedPayload}`;

    const signature = await crypto.subtle.sign(
        {
            name: "ECDSA",
            hash: "SHA-256"
        },
        privateKey,
        utf8(signingInput)
    );

    return (
        signingInput +
        "." +
        base64url(
            new Uint8Array(signature)
        )
    );
}

async function createDPoPProof(
    privateKey: CryptoKey,
    publicJwk: JsonWebKey,
    htu: string,
    htm: string,
    accessToken?: string,
) {
    const header = {
        typ: "dpop+jwt",
        alg: "ES256",
        jwk: {
            kty: publicJwk.kty,
            crv: publicJwk.crv,
            x: publicJwk.x,
            y: publicJwk.y
        }
    };

    const payload = {
        htu,
        htm,
        jti: crypto.randomUUID(),
        iat: Math.floor(Date.now() / 1000),
        ath: (undefined as unknown) as string,
    };

    if (accessToken) {
        const hash = await crypto.subtle.digest(
            "SHA-256",
            utf8(accessToken)
        );

        payload.ath = base64url(
            new Uint8Array(hash)
        );
    }

    return signJwt(
        privateKey,
        header,
        payload
    );
}

export class SolidAuthenticationClient {
    oidcIssuer: string | undefined;
    clientId?: string;
    clientIdLookup: ((issuer: string) => string) | undefined;
    storage: StorageManager;
    authChangeCallbacks: ((authenticated: boolean) => void)[] = [];
    ready: Promise<void>;
    authenticated = false;

    constructor({
        oidcIssuer,
        clientIdLookup,
    }: {
        oidcIssuer?: string;
        clientIdLookup?: (issuer: string) => string;
    } = {}) {
        this.clientIdLookup = clientIdLookup;
        if (clientIdLookup && oidcIssuer) {
            this.clientId = clientIdLookup(oidcIssuer);
        }
        this.storage = new StorageManager("solid-auth-" + oidcIssuer, true);
        const storage = this.storage;
        this.ready = (async () => {
            if (oidcIssuer) {
                await storage.storeValue("issuer", oidcIssuer);
                this.oidcIssuer = oidcIssuer;
            } else {
                this.oidcIssuer = await storage.getValue("issuer");
            }
        })();
    }

    async discoverOidc() {
        if (!this.oidcIssuer) {
            throw new Error("No issuer selected yet.");
        }
        const response = await fetch(
            `${this.oidcIssuer.replace(/\/+$/, "")}/.well-known/openid-configuration`
        );

        if (!response.ok) {
            throw new Error("Discovery failed");
        }

        return response.json();
    }

    async startLogin() {
        const oidc = await this.discoverOidc();
        const pkce = await createPKCE();
        const dpop = await createDPoPKeys();

        const state = randomHex(16);

        const redirectUri =
            location.origin +
            location.pathname;

        if (oidc.registration_endpoint) {
            const client_info = await fetch(
                oidc.registration_endpoint, {
                    method: "POST",
                    body: JSON.stringify({
                        "client_name": "RdfTest",
                        "application_type": "web",
                        "redirect_uris": [redirectUri],
                        "subject_type": "public",
                        "token_endpoint_auth_method": "client_secret_basic",
                        "id_token_signed_response_alg": "RS256",
                        "grant_types": ['authorization_code', 'refresh_token']
                    }),
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            ).then(x => x.json());

            this.clientId = client_info.client_id;
            console.log(client_info);
        }

        await this.storage.update((d: object) => ({
            ...d,
            verifier: pkce.verifier,
            state,
            publicJwk: dpop.publicJwk,
            privateJwk: dpop.privateJwk,
            clientId: this.clientId,
        }));

        const params = new URLSearchParams({
            client_id: this.clientId,
            redirect_uri: redirectUri,
            response_type: "code",
            scope: "openid offline_access webid",
            state,
            code_challenge: pkce.challenge,
            code_challenge_method: "S256",
            prompt: "consent",
            response_mode: "query",
        } as Record<string, string>);

        location.href =
            oidc.authorization_endpoint +
            "?" +
            params;
    }

    async exchangeCode(code: string) {
        const oidc = await this.discoverOidc();
        const { verifier, publicJwk, privateJwk, clientId } = await this.storage.load();
        const privateKey = await importPrivateKey(privateJwk);

        const proof = await createDPoPProof(
            privateKey,
            publicJwk,
            oidc.token_endpoint,
            "POST"
        );

        const redirectUri =
            location.origin +
            location.pathname;

        const body = new URLSearchParams({
            grant_type: "authorization_code",
            redirect_uri: redirectUri,
            code,
            code_verifier: verifier,
            client_id: clientId,
        });

        const response =
            await fetch(
                oidc.token_endpoint,
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/x-www-form-urlencoded",

                        DPoP: proof
                    },
                    body
                },
            );

        if (!response.ok) {
            throw new Error(await response.text());
        }
        return response.json();
    }

    async authenticatedFetch(url: string | RequestInfo | URL, { method = "GET", headers = {}, body = null, ...rest }: RequestInit = {}) {
        const { publicJwk, privateJwk } = await this.storage.load();
        const tokens = await this.getTokens();
        if (!tokens) {
            throw new Error("Not authenticated");
        }
        const accessToken = tokens.access_token;
        const privateKey = await importPrivateKey(privateJwk);

        const proof = await createDPoPProof(
            privateKey,
            publicJwk,
            // @ts-ignore
            url.url || url,
            method,
            accessToken,
        );
        return fetch(url, {
            method,
            body,
            headers: {
                Authorization: `DPoP ${accessToken}`,
                DPoP: proof,
                ...headers
            },
            ...rest
        });
    }
    async getTokens() {
        const tokens = await this.storage.getValue("tokens");
        if (tokens) {
            const expiration = new Date(JSON.parse(atob(tokens.access_token.split(".")[1])).exp*1000);
            if (expiration < new Date()) {
                console.log("Token expired", expiration, new Date());
                // TODO refresh
                return null;
            }
            return tokens;
        }
        return null;
    }
    async handleIncomingRedirect() {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        if (code) {
            if (state !== (await this.storage.getValue("state"))) {
                throw new Error("State mismatch");
            }

            const tokens = await this.exchangeCode(code);
            await this.storage.storeValue("tokens", tokens);
            if (window.location.search) {
                // Construct the clean URL (protocol + host + pathname)
                const cleanURL = window.location.protocol + "//" + window.location.host + window.location.pathname;

                // Update the address bar without reloading
                window.history.replaceState({ path: cleanURL }, '', cleanURL);
            }
            return true;
        } else {
            return false;
        }
    }
    async doFlowsIfNecessary() {
        const tokens = await this.getTokens();
        if (!tokens) {
            if (!await this.handleIncomingRedirect()) {
                if (this.oidcIssuer) {
                    await this.startLogin();
                } else {
                    console.log("Not logging in for solid auth.")
                    this.authChangeCallbacks.forEach(cb => cb(false));
                    this.authenticated = false;
                    return false;
                }
            }
        } else {
            console.log("Authentication check completed");
            this.authChangeCallbacks.forEach(cb => cb(true));
            this.authenticated = true;
            return true;
        }
    }
    async login(oidcIssuer: string) {
        this.oidcIssuer = oidcIssuer;
        await this.storage.storeValue("issuer", oidcIssuer);
        await this.doFlowsIfNecessary();
    }
    onLoginChanged(cb: (authenticated: boolean) => void) {
        this.authChangeCallbacks.push(cb);
    }
}
